import type { Locale, PublicRoute } from './siteConfig';
import { DEFAULT_OG_IMAGE } from './siteConfig';

export interface RouteMetaEntry {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
}

type RouteMetaMap = Record<Locale, Record<PublicRoute, RouteMetaEntry>>;

// Per-locale, per-route SEO copy. Spanish is canonical (LATAM market);
// English mirrors. Titles target ~55 chars, descriptions ~155 chars so the
// snippets render fully in Google SERP and Open Graph previews.
//
// Voice rules applied:
//   - No Title Case in Spanish; capitalize first word + proper nouns only.
//   - Tú forms throughout, no voseo.
//   - Lead with the outcome, not the feature ("Evalúa el 100%…" not
//     "Plataforma de evaluación de llamadas").
//   - Specific over vague — name the regulators and the verticals.

export const ROUTE_META: RouteMetaMap = {
  es: {
    '/': {
      title: 'CallCov · Evalúa el 100% de tus llamadas | Cumplimiento LATAM',
      description:
        'Control operativo de cumplimiento regulatorio y calidad para cobranza, ventas y servicio. Tu equipo define los criterios. CallCov los aplica a cada llamada.',
      keywords:
        'cumplimiento regulatorio llamadas, control de calidad centros de contacto, cobranza, SEDECO, CONDUSEF, LGPD, evaluación de llamadas, transcripción IA, América Latina',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/compliance': {
      title: 'CallCov COMPLY · Cumplimiento regulatorio de llamadas',
      description:
        'Detecta exposición de datos sensibles, omisión de avisos obligatorios, frases prohibidas y desvíos del guion. Mapeado a SEDECO, CONDUSEF, LGPD y más.',
      keywords:
        'cumplimiento centros de contacto, SEDECO Paraguay, CONDUSEF México, Ley 2300 Colombia, LGPD Brasil, evaluación de llamadas',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/quality': {
      title: 'CallCov QUALITY · Control de calidad y entrenamiento automatizado',
      description:
        'Puntuación objetiva en cada llamada y entrenamiento contextual para tus agentes. Sin proyecto largo de calidad, sin planillas.',
      keywords:
        'control de calidad centros de contacto, calidad de llamadas, entrenamiento de agentes, puntuación automatizada',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/pricing': {
      title: 'Precios · Pago por minuto evaluado | CallCov',
      description:
        'Modelo comercial por minuto evaluado. Sin tarifas ocultas. El piloto inicial es sin costo y sin tarjeta de crédito.',
      keywords: 'precios callcov, costo por minuto, piloto sin costo, pricing call center analytics',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/about': {
      title: 'Sobre CallCov · Calidad y cumplimiento para centros de contacto',
      description:
        'Construimos CallCov para profesionalizar el control de calidad y el cumplimiento en los centros de contacto de América Latina. Conoce al equipo.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/contact': {
      title: 'Contacto · Agendar piloto sin costo | CallCov',
      description:
        'Hacemos un piloto sin costo con tus grabaciones. Si no hay impacto, no se avanza. Escríbenos para coordinar.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/support': {
      title: 'Soporte · CallCov',
      description:
        'Documentación, guía de inicio, referencia de API y soporte por email. Respondemos en menos de 24 horas hábiles.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/terms': {
      title: 'Términos del servicio · CallCov',
      description: 'Términos del servicio que rigen el uso de la plataforma CallCov.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/privacy': {
      title: 'Política de privacidad · CallCov',
      description:
        'Cómo CallCov recolecta, procesa y protege los datos de las grabaciones que evaluamos. Diseñado con privacidad y trazabilidad.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
  },
  en: {
    '/': {
      title: 'CallCov · Evaluate 100% of your calls | LATAM Compliance',
      description:
        'Operational compliance and quality for collections, sales and service. Your team defines the criteria. CallCov applies them to every call.',
      keywords:
        'call compliance, QA call center, collections, SEDECO, CONDUSEF, LGPD, call evaluation, AI transcription, LATAM',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/compliance': {
      title: 'CallCov COMPLY · Call compliance built for LATAM',
      description:
        'Catch sensitive-data exposure, missing disclosures, prohibited phrases and script deviations. Mapped to SEDECO, CONDUSEF, LGPD and more.',
      keywords:
        'call center compliance, SEDECO Paraguay, CONDUSEF Mexico, Colombia Law 2300, Brazil LGPD, call evaluation',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/quality': {
      title: 'CallCov QUALITY · Automated QA and coaching',
      description:
        'Objective scoring on every call and contextual coaching for your agents. No long QA project, no spreadsheets.',
      keywords:
        'call QA, contact center quality, agent coaching, automated scoring, contact center quality',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/pricing': {
      title: 'Pricing · Pay per evaluated minute | CallCov',
      description:
        'Commercial model by evaluated minute. No hidden fees. The initial pilot is free and with no credit card.',
      keywords: 'callcov pricing, cost per minute, free pilot, pricing call center analytics',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/about': {
      title: 'About CallCov · QA and compliance for LATAM contact centers',
      description:
        'We built CallCov to professionalize QA and compliance in Latin American contact centers. Meet the team.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/contact': {
      title: 'Contact · Schedule a free pilot | CallCov',
      description:
        'We run a free pilot on your recordings. If there is no impact, we do not move forward. Write to coordinate.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/support': {
      title: 'Support · CallCov',
      description:
        'Documentation, quickstart guide, API reference and email support. We reply in under 24 business hours.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/terms': {
      title: 'Terms of service · CallCov',
      description: 'Terms of service governing the use of the CallCov platform.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
    '/privacy': {
      title: 'Privacy policy · CallCov',
      description:
        'How CallCov collects, processes and protects the data in the recordings we evaluate. Designed with privacy and traceability.',
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
    },
  },
};

export function getRouteMeta(locale: Locale, route: PublicRoute): RouteMetaEntry {
  return ROUTE_META[locale][route];
}
